import React, {
  MouseEvent,
  useState,
} from 'react';
import clsx from 'clsx';
import IconButton from '@mui/material/IconButton';
import PinIcon from 'core/icons/pin';
import Spinner from 'pages/Dashboard/components/Spinner';
import ToolTip from 'core/components/Tooltip';
import {
  SelectOptionProps,
} from 'core/components/Lookup/Generic/DefaultOption';
import compact from 'lodash/compact';
import {
  ServiceCode,
} from 'views/EMR/components/ServiceLookup/type';
import {
  useToggleFavoriteCustomServices,
} from 'views/EMR/components/ServiceLookup/hooks';
import isNil from 'lodash/isNil';
import {
  formatMoney,
} from '@populateui/utils';

type Props = SelectOptionProps<ServiceCode>;

export default function CustomServiceRow({
  option,
  refetch,
  ...props
}: Props) {
  const { mutateAsync: toggleFavorite } = useToggleFavoriteCustomServices();
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const {
    serviceName,
    description,
    isFavorited,
    defaultBilledAmountPerUnit,
  } = option;

  const onToggleFavorite = async (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setIsUpdating(true);
    await toggleFavorite(option);
    await refetch?.();
    setIsUpdating(false);
  };

  return (
    <div
      {...props}
      key={option.serviceName}
      className={clsx(props.className, 'w-full truncate')}
    >
      <div className="flex flex-row gap-2 items-center justify-between w-full">
        <ToolTip
          popperClassName="billing-code-content"
          placement="right"
          title={(
            <span className="block text-xs font-medium">
              {compact([serviceName, description]).join(' - ')}
            </span>
          )}
        >
          <div className="flex flex-col overflow-x-hidden">
            <div className="flex flex-row items-center w-full">
              <IconButton
                disabled={isUpdating}
                className="z-[10] !p-1 is-favorite"
                color="info"
                onClick={onToggleFavorite}
              >
                {isUpdating ? (
                  <Spinner size={16} className="!text-gray-400 !bg-white" />
                ) : (
                  <PinIcon
                    className={clsx('!text-base text-gray-500', {
                      '!text-blue-500': isFavorited,
                    })}
                  />
                )}
              </IconButton>
              <span className={clsx('text-xs ml-2 truncate')}>
                {compact([serviceName, description]).join(' - ')}
              </span>
            </div>
          </div>
        </ToolTip>
        <div className="flex-1 flex justify-end">
          <span className="text-xs text-right font-semibold text-gray-700 whitespace-nowrap min-w-[60px]">
            {!isNil(defaultBilledAmountPerUnit) ? formatMoney(defaultBilledAmountPerUnit) : '\u00A0'}
          </span>
        </div>
      </div>
    </div>
  );
}
